import React, { useEffect } from "react";
import { Button, Grid } from "antd";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

import "./styles.scss";
import logo from "../../Assets/Logos/ibas_logo.jpeg";
import history from "../../../history";

const About = () => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  useEffect(() => {
    AOS.init({
      disable: false,
      startEvent: "DOMContentLoaded",
      initClassName: "aos-init",
      animatedClassName: "aos-animate",
      useClassNames: false,
      disableMutationObserver: false,
      debounceDelay: 50,
      throttleDelay: 99,
      offset: 120,
      delay: 0,
      duration: 500,
      easing: "ease",
      once: false,
      mirror: false,
      anchorPlacement: "top-bottom",
    });

    const timer = setTimeout(() => {
      AOS.refresh();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const scrollToHome = () => {
    history.push("/");
  };

  const values = [
    {
      title: "Excellence",
      description:
        "We are committed to delivering the highest quality education and training programs through innovative teaching methods and expert instructors.",
      icon: "⭐",
    },
    {
      title: "Accessibility",
      description:
        "We believe quality education should be affordable and accessible to everyone, regardless of their socioeconomic background.",
      icon: "🌍",
    },
    {
      title: "Innovation",
      description:
        "We continuously embrace new technologies and teaching methodologies to stay ahead in the ever-evolving educational landscape.",
      icon: "💡",
    },
    {
      title: "Integrity",
      description:
        "We maintain the highest standards of academic and professional integrity in all our endeavors.",
      icon: "🤝",
    },
    {
      title: "Student-Centric",
      description:
        "Our programs are designed with the student at the center, focusing on practical skills and career readiness.",
      icon: "📚",
    },
    {
      title: "Community",
      description:
        "We foster a supportive learning community where students and instructors collaborate for mutual growth.",
      icon: "👥",
    },
  ];

  const stats = [
    { number: "2,500+", label: "Active Students" },
    { number: "15+", label: "Industry Certifications" },
    { number: "50+", label: "Professional Courses" },
    { number: "98%", label: "Student Satisfaction" },
  ];

  const achievements = [
    {
      title: "Government Registered",
      description: "Registered under PV No: 00264413 as a Higher Education Institute",
    },
    {
      title: "AAT Accredited",
      description: "Recognized and accredited by the Association of Accounting Technicians of Sri Lanka (AAT)",
    },
    {
      title: "Industry Partnerships",
      description:
        "Strong partnerships with leading international organizations",
    },
    {
      title: "Expert Faculty",
      description: "Experienced instructors with real-world industry expertise",
    },
  ];

  return (
    <div className="about-page">
      <header className="about-header">
        <div className="header-container">
          <div className="logo">
            <Link to="/" aria-label="IBAS Home">
              <img src={logo} alt="IBAS Logo" />
            </Link>
          </div>
          <nav className="nav-links">
            <Link to="/">Home</Link>
            <Link to="/verify-certificate">Verify Certificate</Link>
            <Link to="/dashboard/cources">Courses</Link>
            <Link to="/login">Sign In</Link>
          </nav>
        </div>
      </header>

      <div className="about-body">
        {/* Hero Section */}
        <section
          className="about-hero"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="hero-content">
            <h1>About IBAS</h1>
            <p>
              Institute of Business and Accounting Studies - Creating
              Professional Leaders for a Better Tomorrow
            </p>
          </div>
        </section>

        {/* Introduction Section */}
        <section
          className="introduction"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="container">
            <div className="intro-grid">
              <div className="intro-text">
                <h2>Who We Are</h2>
                <p>
                  IBAS (Institute of Business and Accounting Studies) is a
                  government-registered higher education institute dedicated to
                  providing world-class business and accounting education. We
                  are committed to equipping students with the knowledge,
                  skills, and certifications needed to excel in their
                  professional careers.
                </p>
                <p>
                  Founded on the principles of excellence, accessibility, and
                  innovation, IBAS has become a trusted learning partner for
                  thousands of students across Sri Lanka and beyond. Our hybrid
                  training model combines the best of online and physical
                  learning experiences.
                </p>
                <Link to="/dashboard/cources" className="cta-button">
                  Explore Our Courses →
                </Link>
              </div>

              <div className="stats-grid">
                {stats.map((stat, idx) => (
                  <div key={idx} className="stat-card" data-aos="zoom-in">
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Mission and Vision */}
        <section
          className="mission-vision"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="container">
            <h2>Our Mission & Vision</h2>

            <div className="mission-vision-grid">
              <div className="card mission-card" data-aos="fade-right">
                <h3>Our Mission</h3>
                <p>
                  To provide innovative, affordable, and accessible quality
                  education that empowers individuals with practical skills and
                  industry-recognized certifications, enabling them to achieve
                  career success and contribute meaningfully to society.
                </p>
              </div>

              <div className="card vision-card" data-aos="fade-left">
                <h3>Our Vision</h3>
                <p>
                  “Creating the Challenging Professional for a Better Working World.”
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section
          className="core-values"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="container">
            <h2>Our Core Values</h2>
            <p className="section-description">
              These principles guide everything we do at IBAS
            </p>

            <div className="values-grid">
              {values.map((value, idx) => (
                <div
                  key={idx}
                  className="value-card"
                  data-aos="zoom-in"
                  data-aos-delay={idx * 100}
                >
                  <div className="value-icon">{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Achievements & Accreditations */}
        <section
          className="achievements"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="container">
            <h2>Achievements & Accreditations</h2>

            <div className="achievements-grid">
              {achievements.map((achievement, idx) => (
                <div
                  key={idx}
                  className="achievement-card"
                  data-aos="fade-up"
                  data-aos-delay={idx * 100}
                >
                  <div className="achievement-icon">✓</div>
                  <h3>{achievement.title}</h3>
                  <p>{achievement.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose IBAS */}
        <section
          className="why-choose"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="container">
            <h2>Why Choose IBAS?</h2>

            <div className="features-list">
              <div className="feature" data-aos="fade-right">
                <div className="feature-number">01</div>
                <div>
                  <h3>Flexible Learning</h3>
                  <p>
                    Online and hybrid programs that fit your schedule, allowing
                    you to balance education with work and personal commitments.
                  </p>
                </div>
              </div>

              <div className="feature" data-aos="fade-left">
                <div className="feature-number">02</div>
                <div>
                  <h3>Expert Instructors</h3>
                  <p>
                    Learn from certified professionals with extensive industry
                    experience and a passion for teaching.
                  </p>
                </div>
              </div>

              <div className="feature" data-aos="fade-right">
                <div className="feature-number">03</div>
                <div>
                  <h3>Industry-Recognized Certifications</h3>
                  <p>
                    Our courses lead to certifications recognized by leading
                    international organizations and employers.
                  </p>
                </div>
              </div>

              <div className="feature" data-aos="fade-left">
                <div className="feature-number">04</div>
                <div>
                  <h3>Lifetime Access</h3>
                  <p>
                    Access to recorded classes and study materials for life,
                    allowing you to revisit content anytime.
                  </p>
                </div>
              </div>

              <div className="feature" data-aos="fade-right">
                <div className="feature-number">05</div>
                <div>
                  <h3>Affordable Pricing</h3>
                  <p>
                    Quality education at competitive prices with flexible
                    payment options to suit your budget.
                  </p>
                </div>
              </div>

              <div className="feature" data-aos="fade-left">
                <div className="feature-number">06</div>
                <div>
                  <h3>Career Support</h3>
                  <p>
                    Personalized guidance and mentoring to help you achieve your
                    career goals and professional aspirations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section
          className="about-cta"
          data-aos="fade-up"
          data-aos-duration="800"
        >
          <div className="cta-content">
            <h2>Ready to Transform Your Career?</h2>
            <p>
              Join thousands of successful students who have advanced their
              careers with IBAS
            </p>
            <div className="cta-buttons">
              <Link to="/dashboard/cources" className="btn btn-primary">
                Explore Courses
              </Link>
              <Link to="/register" className="btn btn-secondary">
                Register Now
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="about-footer">
        <div className="footer-content">
          <p>
            &copy; 2026 IBAS - Institute of Business and Accounting Studies (Pvt) Ltd. All
            rights reserved. Government Registered Higher Education Institute (PV No: 00264413).
          </p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <span>•</span>
            <Link to="/verify-certificate">Verify Certificate</Link>
            <span>•</span>
            <Link to="/dashboard/cources">Courses</Link>
            <span>•</span>
            <a href="/#contact-us">Contact Us</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
